/**
 * Amortize an existing (current) mortgage from balance, rate, and remaining term.
 * Used for keep-vs-refi comparisons on the refinance summary.
 */

export type CurrentLoanAmortizationInput = {
  balance: number;
  annualRatePct: number;
  remainingTermYears: number;
  /** User-entered P&I; when missing/invalid, payment is derived from balance/rate/term. */
  monthlyPayment?: number;
  /** Optional statement payoff; used for cash-to-close style comparisons when > balance. */
  estimatedPayoff?: number;
};

export type CurrentLoanAmortizationResult = {
  remainingMonths: number;
  monthlyPi: number;
  remainingInterest: number;
  remainingPrincipal: number;
  totalRemainingCost: number;
  /** Payoff used for refinance cash math (statement payoff or balance). */
  effectivePayoff: number;
  usedDerivedPayment: boolean;
};

function deriveMonthlyPayment(
  principal: number,
  annualRatePct: number,
  termMonths: number
): number {
  if (principal <= 0 || termMonths <= 0) return 0;
  const monthlyRate = annualRatePct / 100 / 12;
  if (monthlyRate === 0) return principal / termMonths;
  const factor = Math.pow(1 + monthlyRate, termMonths);
  return (principal * (monthlyRate * factor)) / (factor - 1);
}

/**
 * Walk the remaining amortization schedule and sum interest.
 * Caps at remainingMonths; stops early if balance is paid off.
 */
export function computeRemainingInterest(
  balance: number,
  annualRatePct: number,
  monthlyPi: number,
  remainingMonths: number
): number {
  if (balance <= 0 || remainingMonths <= 0 || monthlyPi <= 0) return 0;
  const monthlyRate = annualRatePct / 100 / 12;
  let principal = balance;
  let interestTotal = 0;
  for (let i = 0; i < remainingMonths && principal > 0.01; i += 1) {
    const interest = principal * monthlyRate;
    interestTotal += interest;
    const principalPaid = Math.min(principal, monthlyPi - interest);
    if (principalPaid <= 0) {
      // Payment does not cover interest; treat remaining interest as unbounded
      // for comparison — return interest for scheduled months at current accrual.
      interestTotal += interest * (remainingMonths - i - 1);
      break;
    }
    principal -= principalPaid;
  }
  return Math.max(0, interestTotal);
}

export function amortizeCurrentLoan(
  input: CurrentLoanAmortizationInput
): CurrentLoanAmortizationResult {
  const remainingMonths = Math.max(
    0,
    Math.round((input.remainingTermYears || 0) * 12)
  );
  const balance = Math.max(0, Number(input.balance) || 0);
  const rate = Number(input.annualRatePct) || 0;

  let usedDerivedPayment = false;
  let monthlyPi = Number(input.monthlyPayment) || 0;
  if (monthlyPi <= 0 && balance > 0 && remainingMonths > 0) {
    monthlyPi = deriveMonthlyPayment(balance, rate, remainingMonths);
    usedDerivedPayment = true;
  }

  const remainingInterest = computeRemainingInterest(
    balance,
    rate,
    monthlyPi,
    remainingMonths
  );
  const payoff = Number(input.estimatedPayoff) || 0;
  const effectivePayoff = payoff > 0 ? payoff : balance;

  return {
    remainingMonths,
    monthlyPi,
    remainingInterest,
    remainingPrincipal: balance,
    totalRemainingCost: balance + remainingInterest,
    effectivePayoff,
    usedDerivedPayment,
  };
}
